import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import {
  Person,
  PersonDetail,
  FaceImage,
  PersonLocation,
  PersonVehicle,
  CreatePersonData,
  UpdatePersonData,
  CreatePersonLocationData,
  CreatePersonVehicleData,
  PersonListParams,
  PersonListResponse,
  FaceRecognitionResponse,
  FaceRecognitionParams,
} from '../types';

class PersonService {
  /**
   * Get paginated list of persons with filters
   */
  async getPersons(params?: PersonListParams): Promise<PersonListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.person_type) queryParams.append('person_type', params.person_type);
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    
    const queryString = queryParams.toString();
    const url = `${API_CONFIG.ENDPOINTS.PERSONS}${queryString ? `?${queryString}` : ''}`;
    
    return apiClient.get<PersonListResponse>(url);
  }

  /**
   * Get person by ID (basic info)
   */
  async getPersonById(personId: number): Promise<Person> {
    return apiClient.get<Person>(`${API_CONFIG.ENDPOINTS.PERSONS}/${personId}`);
  }

  /**
   * Get person detail with all related data (faces, locations, vehicles)
   */
  async getPersonDetail(personId: number): Promise<PersonDetail> {
    return apiClient.get<PersonDetail>(`${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/detail`);
  }

  /**
   * Create a new person
   */
  async createPerson(data: CreatePersonData): Promise<Person> {
    return apiClient.post<Person>(API_CONFIG.ENDPOINTS.PERSONS, data);
  }

  /**
   * Update an existing person
   */
  async updatePerson(personId: number, data: UpdatePersonData): Promise<Person> {
    return apiClient.put<Person>(`${API_CONFIG.ENDPOINTS.PERSONS}/${personId}`, data);
  }

  /**
   * Delete a person
   */
  async deletePerson(personId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${API_CONFIG.ENDPOINTS.PERSONS}/${personId}`);
  }

  // ============ Face Images ============

  /**
   * Get all face images for a person
   */
  async getFaceImages(personId: number): Promise<FaceImage[]> {
    const response = await apiClient.get<{ items: FaceImage[] }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/faces`
    );
    return response.items || [];
  }

  /**
   * Upload a face image for a person
   */
  async uploadFaceImage(personId: number, file: File, isPrimary: boolean = false): Promise<FaceImage> {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/faces?is_primary=${isPrimary}`;
    return apiClient.post<FaceImage>(url, formData);
  }

  /**
   * Delete a face image
   */
  async deleteFaceImage(personId: number, imageId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/faces/${imageId}`
    );
  }

  // ============ Location Access ============

  /**
   * Get all location access records for a person
   */
  async getPersonLocations(personId: number): Promise<PersonLocation[]> {
    const response = await apiClient.get<{ items: PersonLocation[] }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/locations`
    );
    return response.items || [];
  }

  /**
   * Grant location access to a person
   */
  async grantLocationAccess(personId: number, data: CreatePersonLocationData): Promise<PersonLocation> {
    return apiClient.post<PersonLocation>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/locations`,
      data
    );
  }

  /**
   * Revoke location access from a person
   */
  async revokeLocationAccess(personId: number, locationId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/locations/${locationId}`
    );
  }

  // ============ Vehicles ============

  /**
   * Get all vehicles for a person
   */
  async getPersonVehicles(personId: number): Promise<PersonVehicle[]> {
    const response = await apiClient.get<{ items: PersonVehicle[] }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/vehicles`
    );
    return response.items || [];
  }

  /**
   * Add a vehicle to a person
   */
  async addVehicle(personId: number, data: CreatePersonVehicleData): Promise<PersonVehicle> {
    return apiClient.post<PersonVehicle>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/vehicles`,
      data
    );
  }

  /**
   * Remove a vehicle from a person
   */
  async removeVehicle(personId: number, vehicleId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `${API_CONFIG.ENDPOINTS.PERSONS}/${personId}/vehicles/${vehicleId}`
    );
  }

  // ============ Lookup ============

  /**
   * Lookup person by plate number
   */
  async lookupByPlate(plateNumber: string): Promise<Person | null> {
    try {
      return await apiClient.get<Person>(
        `${API_CONFIG.ENDPOINTS.PERSON_LOOKUP_PLATE}?plate_number=${encodeURIComponent(plateNumber)}`
      );
    } catch (error: any) {
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // ============ Face Recognition ============

  /**
   * Recognize faces in an image
   */
  async recognizeFace(
    file: File,
    params?: FaceRecognitionParams
  ): Promise<FaceRecognitionResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const queryParams = new URLSearchParams();
    if (params?.location_id) queryParams.append('location_id', params.location_id.toString());
    if (params?.threshold) queryParams.append('threshold', params.threshold.toString());
    if (params?.max_results) queryParams.append('max_results', params.max_results.toString());
    
    const queryString = queryParams.toString();
    const url = `${API_CONFIG.ENDPOINTS.PERSONS}/recognize${queryString ? `?${queryString}` : ''}`;
    
    return apiClient.post<FaceRecognitionResponse>(url, formData);
  }
}

export const personService = new PersonService();
